import React, { useRef, useState } from "react";
import { Box, Grid, IconButton, Tooltip, Snackbar, Alert } from "@mui/material";
import SaveAltTwoToneIcon from "@mui/icons-material/SaveAltTwoTone";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

const ImageViewer = ({ imagefile, width = 320 }) => {
  const baseSrc = imagefile.surl || imagefile.url || imagefile.cdn_url || imagefile.turl;
  const src = baseSrc || '';
  
  const lref = useRef();
  const [zoom, setZoom] = useState(1);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const getUrlWithWidth = (url) => {
    if (!url) return '';
    return url.includes('?') ? `${url}&width=2048` : `${url}?width=2048`;
  };

  const onClickDownload = async () => {
    if (!src) {
      showSnackbar("Invalid or missing image URL", "error");
      return;
    }
    
    const downloadUrl = getUrlWithWidth(src);
    showSnackbar("Download started...", "info");
    
    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = src.split("/").pop().split('?')[0]; // Remove query params from filename
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar("Download successful!", "success");
    } catch (error) {
      console.warn("Fetch download failed due to CORS or network:", error);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = src.split("/").pop().split('?')[0]; // Remove query params from filename
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      a.remove();
      showSnackbar("Download triggered via fallback.", "warning");
    }
  };

  const onClickOpen = () => {
    const openUrl = getUrlWithWidth(src);
    lref.current.href = openUrl;
    lref.current.click();
  };

  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const zoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.3));

  const buttonBaseStyle = {
    fontSize: "0.70rem",
    borderRadius: 2,
    color: "white",
    minHeight: 28,
    minWidth: 28,
    padding: "2px 8px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    lineHeight: 1.2,
  };


  const openButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: "#0ABAB5",
    "&:hover": {
      backgroundColor: "#0ab1ac",
    },
  };

  const downloadButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: "#4ea5f9",
    "&:hover": {
      backgroundColor: "#509af4",
    },
  };

  // const zoomButtonStyle = {
  //   ...buttonBaseStyle,
  //   backgroundColor: "#607d8b",
  //   "&:hover": {
  //     backgroundColor: "#455a64",
  //   },
  // };

  return (
    <Box sx={{ width: "100%", maxWidth: width, margin: "0 auto" }}>
      <Box
        sx={{
          overflow: "auto",
          textAlign: "center",
          mb: 1,
        }}
      >
        <img
          src={src}
          alt={imagefile.filename || "Image"}
          style={{
            // width: `${zoom * 100}%`,
            width: "600px",
            maxWidth: "100%",
            borderRadius: 8,
            background: "#f5f5f5",
            transition: "width 0.2s",
          }}
        />
      </Box>
      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ mt: 1 }}
      >
        <Grid item>
          <Tooltip title="Open" arrow>
            <IconButton size="small" onClick={onClickOpen} sx={openButtonStyle}>
              <FileOpenIcon fontSize="small" sx={{ marginRight: 0.5 }} />
              <span style={{ fontSize: "0.70rem" }}>Open</span>
            </IconButton>
          </Tooltip>
        </Grid>
        <a
          ref={lref}
          style={{ display: "none" }}
          href={src}
          target="_blank"
          rel="noreferrer"
        />
        <Grid item>
          <Tooltip title="Download" arrow>
            <IconButton
              size="small"
              onClick={onClickDownload}
              sx={downloadButtonStyle}
            >
              <SaveAltTwoToneIcon fontSize="small" sx={{ marginRight: 0.3 }} />
              <span style={{ fontSize: "0.70rem" }}>Download</span>
            </IconButton>
          </Tooltip>
        </Grid>
        {/* <Grid item>
          <Tooltip title="Zoom In" arrow>
            <IconButton size="small" onClick={zoomIn} sx={zoomButtonStyle}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item>
          <Tooltip title="Zoom Out" arrow>
            <IconButton size="small" onClick={zoomOut} sx={zoomButtonStyle}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid> */}
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ImageViewer;
